/**
 * Comment Remapper
 * Flutter source: lib/data/models/comment.dart
 */

import type { CommentDto } from '@/data/dtos/comment/commentDto';
import type { JsonObject } from '@/data/types/json';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';
import { CommentReplyRemapper } from '@/domain/remappers/comment/commentReplyRemapper';
import { MediaInfoRemapper } from '@/domain/remappers/info/mediaInfoRemapper';

export class CommentRemapper {
  static fromJson(data: JsonObject): CommentDto {
    const commentToReply = data['commentToReply'] as JsonObject | undefined;
    const mediaInfo = data['mediaInfo'] as JsonObject | undefined;
    return {
      id: (data['id'] as string | undefined) || '',
      parentId: (data['parentId'] as string | null | undefined) ?? null,
      text: (data['text'] as string | undefined) || '',
      commentToReply: commentToReply
        ? CommentReplyRemapper.fromJson(commentToReply)
        : null,
      replies: (data['replies'] as number | undefined) ?? 0,
      likes: (data['likes'] as number | undefined) ?? 0,
      popularity: (data['popularity'] as number | undefined) ?? 0,
      createdTimestamp: (data['createdTimestamp'] as number | undefined) ?? 0,
      mediaPath: (data['mediaPath'] as string | undefined) || '',
      userId: (data['userId'] as string | undefined) || '',
      contentUserId: (data['contentUserId'] as string | undefined) || '',
      userName: (data['userName'] as string | undefined) || '',
      username: (data['username'] as string | undefined) || '',
      mediaInfo: mediaInfo ? MediaInfoRemapper.fromJson(mediaInfo) : null,
    };
  }

  static toEntity(data: any): CommentEntity {
    const dto = CommentRemapper.fromJson(data);
    return {
      id: dto.id,
      parentId: dto.parentId ?? null,
      text: dto.text,
      commentToReply: dto.commentToReply
        ? CommentReplyRemapper.toEntity(dto.commentToReply)
        : null,
      replies: dto.replies,
      likes: dto.likes,
      popularity: dto.popularity,
      createdTimestamp: dto.createdTimestamp,
      mediaPath: dto.mediaPath,
      userId: dto.userId,
      contentUserId: dto.contentUserId,
      userName: dto.userName,
      username: dto.username,
      mediaInfo: dto.mediaInfo ? MediaInfoRemapper.toEntity(dto.mediaInfo) : null,
      parent: null,
      children: [],
      lastFetchedChild: null,
      hasMoreChildren: true,
      childrenFetched: 0,
      isLastChild: false,
      isFetchingChildren: false,
      isInitialComment: false,
    };
  }

  static toDto(entity: CommentEntity): CommentDto {
    return {
      id: entity.id,
      parentId: entity.parentId ?? null,
      text: entity.text,
      commentToReply: entity.commentToReply ? CommentReplyRemapper.toDto(entity.commentToReply) : null,
      replies: entity.replies,
      likes: entity.likes,
      popularity: entity.popularity,
      createdTimestamp: entity.createdTimestamp,
      mediaPath: entity.mediaPath,
      userId: entity.userId,
      contentUserId: entity.contentUserId,
      userName: entity.userName,
      username: entity.username,
      mediaInfo: entity.mediaInfo ? MediaInfoRemapper.toDto(entity.mediaInfo) : null,
    };
  }

  /**
   * Plain JSON object for backend writes (no undefined values — an explicit
   * `null` for every unset field, per Flutter's generated `toJson`, which has
   * no `includeIfNull: false` on `Comment` (`comment.dart:8`)).
   */
  static toJson(dto: CommentDto): JsonObject {
    return {
      id: dto.id,
      parentId: dto.parentId ?? null,
      text: dto.text,
      commentToReply: dto.commentToReply ? CommentReplyRemapper.toJson(dto.commentToReply) : null,
      replies: dto.replies,
      likes: dto.likes,
      popularity: dto.popularity,
      createdTimestamp: dto.createdTimestamp,
      mediaPath: dto.mediaPath,
      userId: dto.userId,
      contentUserId: dto.contentUserId,
      userName: dto.userName,
      username: dto.username,
      // Flutter `comment.g.dart:46` — `'mediaInfo': instance.mediaInfo?.toJson()`.
      // The key is ALWAYS written (null when unset); there is no
      // `includeIfNull: false` on `Comment` (`comment.dart:8`), so a Flutter-written
      // text comment carries `mediaInfo: null` and the web write must match that
      // document shape. The nested model goes through MediaInfoRemapper.toJson so
      // all 14 keys are emitted with explicit nulls for the unset optionals
      // (`media_info.g.dart:27-42`). Assigning the DTO verbatim would leak
      // `undefined` optionals into the Firestore write and reject the whole
      // transaction. Same convention as `notificationInfoRemapper.ts:130`.
      mediaInfo: dto.mediaInfo ? MediaInfoRemapper.toJson(dto.mediaInfo) : null,
    };
  }
}
